import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Phone, Car, IdCard, AlertCircle, ExternalLink, Heart, Leaf, Smartphone } from 'lucide-react';

const ResponsibleConsumptionSection = () => {
  return (
    <section
      id="responsible"
      className="py-16 sm:py-20 bg-gradient-to-b from-black via-gray-950 to-black border-t border-white/5"
      data-testid="responsible-section"
    >
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 mb-4">
            <ShieldAlert className="w-4 h-4 text-pink-400" />
            <span className="text-pink-300 text-sm font-medium tracking-wider uppercase">Drink Responsibly</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
            Have fun. Stay safe. 21+ only.
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto leading-relaxed">
            The Aloha Tequila & Arts Festival is a celebration of agave culture for adults of legal drinking age.
            We&apos;re committed to a safe, respectful, and responsible event for every guest.
          </p>
        </div>

        {/* Safe Ride Hawaii partner block */}
        <div className="mb-10 bg-gradient-to-br from-red-500/15 via-orange-500/10 to-pink-500/10 border border-red-500/30 rounded-3xl p-6 sm:p-8" data-testid="safe-ride-hawaii-block">
          <div className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-7 items-center">
            <a
              href="https://www.saferidehawaii.org/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="safe-ride-hawaii-logo-link"
              className="flex justify-center sm:justify-start"
              aria-label="Visit Safe Ride Hawaii"
            >
              <img
                src="/partners/saferide-hawaii.png"
                alt="Safe Ride Hawaii"
                className="w-32 h-32 sm:w-36 sm:h-36 object-contain rounded-2xl shadow-2xl shadow-red-500/30 transition-transform hover:scale-[1.03]"
                loading="lazy"
              />
            </a>
            <div>
              <div className="inline-flex items-center gap-1.5 bg-red-500/15 border border-red-500/35 text-red-300 text-[11px] sm:text-xs font-bold tracking-wider uppercase px-3 py-1 rounded-full mb-3">
                <Heart className="w-3 h-3" /> Proud Safety Partner
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white mb-2 leading-tight">
                Thrilled to partner with <span className="text-red-300">Safe Ride Hawaii</span>
              </h3>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-4">
                Don&apos;t drink and drive. We strongly encourage every guest to use Uber, Lyft, or a designated driver. Our goal is simple — <strong className="text-white">save lives, be good partners in everything we do, and leave no trace.</strong>
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.saferidehawaii.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="safe-ride-hawaii-visit"
                  className="inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold text-sm px-4 py-2 rounded-full transition-all"
                >
                  Visit Safe Ride Hawaii
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a
                  href="https://apps.apple.com/us/app/saferidenow-app/id1336638589"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="safe-ride-app-download"
                  className="inline-flex items-center gap-1.5 bg-white text-gray-900 hover:bg-red-50 font-bold text-sm px-4 py-2 rounded-full transition-all shadow-sm"
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  Download App
                </a>
                <a
                  href="https://m.uber.com/?action=setPickup&pickup=my_location&dropoff[formatted_address]=International%20Market%20Place%2C%20Honolulu%2C%20HI"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="safe-ride-uber"
                  className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold text-sm px-4 py-2 rounded-full transition-all"
                >
                  <Car className="w-3.5 h-3.5" /> Open Uber
                </a>
                <a
                  href="https://ride.lyft.com/ridetype?destination%5Baddress%5D=International%20Market%20Place%2C%20Honolulu%2C%20HI"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="safe-ride-lyft"
                  className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-bold text-sm px-4 py-2 rounded-full transition-all"
                >
                  <Car className="w-3.5 h-3.5" /> Open Lyft
                </a>
              </div>
              <p className="text-white/55 text-xs sm:text-sm mt-4 inline-flex items-center gap-1.5">
                <Leaf className="w-3.5 h-3.5 text-emerald-400" />
                Save lives. Be good partners. Leave no trace.
              </p>
            </div>
          </div>
        </div>

        {/* Key compliance points */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            {
              icon: IdCard,
              title: '21+ with Valid ID',
              body: 'Must be 21 years of age or older. Valid government-issued photo ID required at entry. No exceptions.'
            },
            {
              icon: ShieldAlert,
              title: 'Know Your Limits',
              body: 'Pace yourself between pours, drink water, and eat. Our staff may decline service to overserved guests.'
            },
            {
              icon: Car,
              title: "Don't Drive Impaired",
              body: 'Use a rideshare, designated driver, or stay nearby. International Market Place is walkable to most Waikiki hotels.'
            },
            {
              icon: AlertCircle,
              title: 'No Refunds',
              body: 'All ticket sales are final. Tickets are transferable to attendees aged 21+. Review our Terms before purchase.'
            }
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={i}
                data-testid={`responsible-card-${i}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-pink-500/40 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500/20 to-orange-500/20 border border-pink-500/30 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-pink-400" />
                </div>
                <h3 className="text-white font-bold text-base mb-2">{item.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{item.body}</p>
              </div>
            );
          })}
        </div>

        {/* Help resources */}
        <div className="bg-gradient-to-br from-pink-500/10 to-orange-500/5 border border-pink-500/30 rounded-3xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-xl bg-pink-500/20 border border-pink-500/40 items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-pink-300" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg mb-2">Need help with alcohol use?</h3>
              <p className="text-white/75 text-sm leading-relaxed mb-4">
                If you or someone you know is struggling with alcohol, free and confidential help is available 24/7
                from the SAMHSA National Helpline.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:1-800-662-4357"
                  data-testid="responsible-samhsa-link"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 hover:bg-pink-100 px-4 py-2 rounded-full text-sm font-bold transition-all"
                >
                  <Phone className="w-4 h-4" />
                  1-800-662-HELP (4357)
                </a>
                <a
                  href="https://www.samhsa.gov/find-help/national-helpline"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="responsible-samhsa-web"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 text-white px-4 py-2 rounded-full text-sm font-bold transition-all border border-white/20"
                >
                  SAMHSA.gov
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Legal links */}
        <div className="mt-8 text-center text-white/50 text-xs sm:text-sm">
          By purchasing a ticket you agree to our{' '}
          <Link to="/terms" className="text-pink-400 hover:text-pink-300 underline" data-testid="responsible-terms-link">Terms & Conditions</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-pink-400 hover:text-pink-300 underline" data-testid="responsible-privacy-link">Privacy Policy</Link>.
          {' '}Tickets are non-refundable but may be transferred to a guest aged 21+.
        </div>
      </div>
    </section>
  );
};

export default ResponsibleConsumptionSection;
